import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Card, 
  Tabs, 
  Spin, 
  Typography, 
  notification,
  Button, 
  Space, 
  Result,
  Breadcrumb
} from 'antd';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import { useProductPropertiesById, useUpdateProductProperties } from '../hooks/useProductProperties';
import { ProductPropertiesDTO } from '@/shared/api/model';
import LoanProductSettingsForm from '../components/LoanProductSettingsForm';
import FeesConfigPanel from '../components/FeesConfigPanel';
import GLAccountMappingsPanel from '../components/GLAccountMappingsPanel';
import FormTemplateConfigPanel from '../components/FormTemplateConfigPanel';
import LoanProductPreviewPanel from '../components/LoanProductPreviewPanel';
import { useGetAllFeesByProductPropertiesGlNo } from '@/shared/api/client/fee-resource';

const { Title } = Typography;
const { TabPane } = Tabs;

const LoanProductSettingsDetailView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('basic');
  const [loading, setLoading] = useState(false);

  // Debug the route parameter
  console.log('Route parameter id:', id);

  // Fetch product properties data
  const { 
    data: productProperties, 
    isLoading, 
    isError,
    error 
  } = useProductPropertiesById(id);

  // Fetch fees for this product
  const { data: feesData } = useGetAllFeesByProductPropertiesGlNo(
    id as string,
      // @ts-ignore
    { enabled: !!id }
  );

  // Add more debugging
  console.log('API response:', productProperties);

  // Update mutation
  const updateProductProperties = useUpdateProductProperties();

  // Handle form submission
  const handleSubmit = async (values: ProductPropertiesDTO) => {
    try {
      setLoading(true);
      
      // Validate ID exists
      if (!id) {
        throw new Error('Product ID is missing');
      }

      // Get the current fees to include in the update
      const currentFees = feesData || [];

      // Create the proper data object
      const updatedProductProperties: ProductPropertiesDTO = {
        ...productProperties,
        ...values,                   // Ensure form values override existing data
        fees: currentFees,
        glNo: id
      };
      
      // Ensure required documents is properly formatted as a JSON string if it's an array
      if (Array.isArray(updatedProductProperties.requiredDocuments)) {
        updatedProductProperties.requiredDocuments = JSON.stringify(updatedProductProperties.requiredDocuments);
      }
      
      // Use the structure expected by the Orval-generated client
      await updateProductProperties.mutateAsync({
        glNo: id!,
        data: updatedProductProperties
      });
      
      notification.success({
        message: 'Success',
        description: 'Product properties updated successfully',
      });
    } catch (error) {
      console.error('Failed to update product properties:', error);
      notification.error({
        message: 'Error',
        description: 'Failed to update product properties. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle form template configuration save
  const handleFormTemplateSave = async (configJson: string, logoUrl?: string, watermarkUrl?: string) => {
    try {
      setLoading(true);
      
      if (!id) {
        throw new Error('Product ID is missing');
      }

      // Get the current fees to include in the update
      const currentFees = feesData || [];
      
      // Create the update with form configuration
      // @ts-ignore
      const updatedProductProperties: ProductPropertiesDTO = {
        ...productProperties,
        fees: currentFees,
        glNo: id,
        loanFormConfig: configJson,
        loanFormLogoUrl: logoUrl,
        loanFormWatermarkUrl: watermarkUrl
      };
      
      await updateProductProperties.mutateAsync({
        glNo: id!,
        data: updatedProductProperties
      });
      
      notification.success({
        message: 'Success',
        description: 'Form template configuration updated successfully',
      });
    } catch (error) {
      console.error('Failed to update form template configuration:', error);
      notification.error({
        message: 'Error',
        description: 'Failed to update form template configuration. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/admin/loan-settings/products');
  };

  // If loading, show spinner
  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }
  
  // Add detailed logging to debug the response
  console.log('Product Properties Response:', productProperties);
  console.log('Response type:', typeof productProperties);
  console.log('Response structure:', productProperties ? 'Has data property' : 'No data property');

  // If error, show error message
  if (isError || !productProperties) {
    return (
      <Result
        status="error"
        title="Failed to load product properties"
        subTitle={error instanceof Error ? error.message : "Please try again or contact the administrator."}
        extra={[
          <Button type="primary" key="back" onClick={handleBack}>
            Back to List
          </Button>,
        ]}
      />
    );
  }

  return (
    <div>
      {/* Breadcrumb and header */}
      <div style={{ marginBottom: 16 }}>
        <Breadcrumb items={[
          { title: 'Loan Product Settings', href: '/admin/loan-settings/products' },
          { title: productProperties.productName || productProperties.glNo },
        ]} />
        
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
          <Title level={4}>{productProperties.productName || 'Product Configuration'}</Title>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
              Back to List
            </Button>
            <Button 
              type="primary" 
              icon={<SaveOutlined />} 
              onClick={() => document.getElementById('product-form-submit')?.click()}
              loading={loading}
            >
              Save Changes
            </Button>
          </Space>
        </div>
      </div>

      {/* Main content */}
      <Card>
        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab}
          type="card"
        >
          <TabPane tab="Basic Settings" key="basic">
            <LoanProductSettingsForm 
              initialData={productProperties}
              onSubmit={handleSubmit}
              isLoading={loading}
            />
          </TabPane>
          <TabPane tab="Interest & Fees" key="fees">
            <FeesConfigPanel 
              productGlNo={productProperties.glNo}
              productName={productProperties.productName||''}
            />
          </TabPane>
          <TabPane tab="GL Account Mappings" key="gl">
            <GLAccountMappingsPanel
              productGlNo={productProperties.glNo}
              initialData={productProperties}
            />
          </TabPane>
          <TabPane tab="Application Form" key="application-form-template">
            <FormTemplateConfigPanel
              glNo={productProperties.glNo}
              initialConfig={productProperties.loanFormConfig}
              logoUrl={productProperties.loanFormLogoUrl}
              watermarkUrl={productProperties.loanFormWatermarkUrl}
              editorMode="application"
              onSave={handleFormTemplateSave}
            />
          </TabPane>
          <TabPane tab="Deduction Form" key="deduction-form-template">
            <FormTemplateConfigPanel
              glNo={productProperties.glNo}
              initialConfig={productProperties.loanFormConfig}
              logoUrl={productProperties.loanFormLogoUrl}
              watermarkUrl={productProperties.loanFormWatermarkUrl}
              editorMode="deduction"
              onSave={handleFormTemplateSave}
            />
          </TabPane>
          <TabPane tab="Advanced Settings" key="advanced">
            <p>Advanced settings will be implemented in the next phase.</p>
          </TabPane>
          <TabPane tab="Preview Schedule" key="preview">
            <LoanProductPreviewPanel
              glNo={productProperties.glNo}
              productName={productProperties.productName || productProperties.glNo}
              baseInterestRate={Number(productProperties.baseInterestRate)}
              defaultAmount={Number(productProperties.defaultAmount)}
              defaultTerm={productProperties.defaultTerm ?? undefined}
              requiresDeductionForm={productProperties.requiresDeductionForm ?? false}
            />
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

export default LoanProductSettingsDetailView;
